import { createApi } from "@reduxjs/toolkit/query/react";
import { createBaseQueryWithLogoutGuard } from "../rtk-query/common/createBaseQueryWithLogoutGuard";
import { store } from "../app/store";
import { fetchCurrentCachedState } from "../utils/Utils";
import {
  CLUSTER_OBJECTS,
  transformFilterData,
} from "./newClusterSlice";

const getState = () => store.getState();
const selectFromState = (selector) => selector(getState());

export const _authToken = () => {
  const state = getState();
  return `${state?.authReducer?.userData?.tokenType?.toLowerCase()} ${
    state?.authReducer?.userData?.token
  }`;
};

export const _orgClientId = () =>
  selectFromState((state) => state?.orgReducer?.selectedFI?.orgClientId);

export const _hid = () =>
  selectFromState((state) => state?.orgReducer?.healthAnalysisID?.uid);

export const _contextualFilterObj = () =>
  selectFromState((state) => state?.newClusterReducer?.contextualFilterObj);

export const _currentDashboard = () =>
  selectFromState((state) => state?.dashboardReducer?.currentDashboard);

export const _clusterApiKey = () =>
  selectFromState(
    (state) => state?.dashboardReducer?.selectedCard?.clusterObject?.clusterApiKey,
  );

const getAuthHeaders = () => ({
  Authorization: _authToken(),
  "Content-Type": "application/json",
});

const hasInitialFilterCache = (cachedInitialFilterData) =>
  cachedInitialFilterData != null &&
  Object.keys(cachedInitialFilterData).length > 0;

const fetchInitialFilterDataFromApi = async () => {
  return store
    .dispatch(
      newInitialFilterDataSlice.endpoints.getInitialFilterData.initiate({
        headers: getAuthHeaders(),
      }),
    )
    .unwrap();
};

const getInitialFilterDataSource = async () => {
  const cachedInitialFilterData = fetchCurrentCachedState(
    getNewInitialFilterQueriesCache(),
  )?.getInitialFilterData;

  if (hasInitialFilterCache(cachedInitialFilterData)) {
    return {
      sourceData: cachedInitialFilterData,
      fromApi: false,
    };
  }

  const sourceData = await fetchInitialFilterDataFromApi();
  return {
    sourceData,
    fromApi: true,
  };
};

const resolveFilterDataSource = ({ sourceData, fromApi }) =>
  fromApi ? sourceData?.data : sourceData;

const getCurrentClusterObject = () => CLUSTER_OBJECTS?.[_clusterApiKey()] || {};

const getNewInitialFilterQueriesCache = () => {
  const state = getState();

  // Prefer the actual reducerPath and keep legacy fallback for safety.
  return (
    state?.[newInitialFilterDataSlice.reducerPath]?.queries ||
    state?.filter_data?.queries ||
    {}
  );
};

export const getGraphObjectsFromInitialFilter = async () => {
  const { legendsKey, zonesKey } = getCurrentClusterObject();

  const { sourceData, fromApi } = await getInitialFilterDataSource();
  const filterData = resolveFilterDataSource({ sourceData, fromApi });
  const graphItems = filterData?.[legendsKey] ?? null;
  const zoneItems = filterData?.[zonesKey] ?? null;

  return { graphItems, zoneItems };
};

export const getContextualObjectsFromInitialFilter = async () => {
  const contextualFilterObj = _contextualFilterObj();
  const { sourceData, fromApi } = await getInitialFilterDataSource();
  const filterData = resolveFilterDataSource({ sourceData, fromApi });
  const filterItems = filterData?.[contextualFilterObj?.filterkey];

  const filterMap = filterItems?.data
    ? new Map(filterItems.data.map((item) => [item.id, item]))
    : null;

  return { filterMap };
};

export const newInitialFilterDataSlice = createApi({
  reducerPath: "new_initial_filter_data_slice",
  baseQuery: createBaseQueryWithLogoutGuard({
    baseUrl: process.env.REACT_APP_API_URL,
  }),
  tagTypes: ["filter_data"],
  endpoints: (build) => ({
    getInitialFilterData: build.query({
      query: ({ headers }) => ({
        url: `bank/${_orgClientId()}/drilldown/${_hid()}/${_clusterApiKey()}/filter`,
        method: "POST",
        headers,
        body: {
          filter: {},
          requid: _hid(),
          producttype: _currentDashboard()?.KEY,
          gte: "",
          lte: "",
          duration: "",
        },
      }),
      transformResponse: (response) => {
        return { ...response, data: transformFilterData(response?.data) };
      },
    }),
  }),
});

export const { useGetInitialFilterDataQuery } = newInitialFilterDataSlice;
